import { Loading, useDataQuery } from "@lyfegen-platform/react-common";
import { Box, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import startUpper from "../../../../utils/start-upper";
import { GET_DEFAULT_AGREEMENT_ROLES } from "../queries";
import useOrganizationStyles from "../use-organization-styles";
import { SelectableValue } from "../../../../models/common/selectable-value";
import { Fragment, useEffect, useState } from "react";
import DefaultAgreementRolesRow from "./DefaultAgreementRolesRow";
import { groupBy, sortBySelectors } from "@lyfegen-platform/utils";
import { GetDefaultAgreementRolesQuery } from "../types/queries.generated";

interface DefaultAgreementRolesActionProps {
	orgId: string;
	selectedUsers: Array<SelectableValue>;
	selectedTags: Array<SelectableValue>;
}

const DefaultAgreementRolesAction: React.FC<DefaultAgreementRolesActionProps> = ({ orgId, selectedUsers, selectedTags }) => {
	const { t } = useTranslation();
	const [defaultAgreementRoles, setDefaultAgreementRoles] =
		useState<[string, GetDefaultAgreementRolesQuery["GetDefaultAgreementRoles"]][]>();
	const [filteredData, setFilteredData] = useState<[string, GetDefaultAgreementRolesQuery["GetDefaultAgreementRoles"]][]>();
	const { loading, error, refetch } = useDataQuery<GetDefaultAgreementRolesQuery>(GET_DEFAULT_AGREEMENT_ROLES, {
		variables: { input: { id: orgId, tags: [], users: [], domains: [] } },
		fetchPolicy: "no-cache",
		onCompleted: (tempData) => {
			const newState = Array.from(
				groupBy(
					(tempData?.GetDefaultAgreementRoles || [])
						.slice()
						.sort(sortBySelectors((element) => (element?.lastName || "") + (element?.firstName || ""))),
					(r: any) => r.lastName + " " + r.firstName
				)
			);
			setDefaultAgreementRoles(newState);
			setFilteredData(getNewFilteredData(newState));
		},
	});

	const getNewFilteredData = (data: [string, GetDefaultAgreementRolesQuery["GetDefaultAgreementRoles"]][] | undefined) => {
		const users = selectedUsers.map((user) => user.label);
		const tags = selectedTags.map((tag) => tag.label);

		return data
			?.map((defaultAgreementRole) => {
				if (users.length && !users.includes(defaultAgreementRole[0])) return null;

				const agreementRoles = defaultAgreementRole[1].filter(
					(agreementRole) => !tags.length || tags.includes(agreementRole.tagName)
				);

				if (!agreementRoles.length) return null;

				return [defaultAgreementRole[0], agreementRoles];
			})
			.filter(Boolean) as [];
	};

	const { agreementsTable, defaultAgreementRolesContainer } = useOrganizationStyles();

	useEffect(() => {
		setFilteredData(getNewFilteredData(defaultAgreementRoles));
	}, [selectedUsers, selectedTags]);

	if (error) return <Typography color="error">Error...{console.error(error)}</Typography>;

	return (
		<Fragment>
			<Box hidden={loading} className={defaultAgreementRolesContainer}>
				<table className={agreementsTable}>
					<colgroup>
						<col width="13%" />
						<col width="13%" />
						<col width="13%" />
						<col width="13%" />
						<col width="13%" />
						<col width="13%" />
						<col width="13%" />
					</colgroup>
					<thead>
						<tr>
							<th>
								<Box display="flex" alignItems="center">
									{startUpper(t("username"))}
								</Box>
							</th>
							<th>
								<Box display="flex" alignItems="center">
									{startUpper(t("agreement-tag"))}
								</Box>
							</th>
							<th>
								<Box display="flex" alignItems="center">
									{startUpper(t("default-agreement-role"))}
								</Box>
							</th>
							<th>
								<Box display="flex" alignItems="center">
									{startUpper(t("agreement-assignee"))}
								</Box>
							</th>
							<th>
								<Box display="flex" alignItems="center">
									{startUpper(t("case-assignee"))}
								</Box>
							</th>
							<th>
								<Box display="flex" alignItems="center">
									{startUpper(t("document-assignee"))}
								</Box>
							</th>
							<th>
								<Box display="flex" alignItems="center">
									{startUpper(t("apply-to-all-items"))}
								</Box>
							</th>
						</tr>
					</thead>
					<tbody>
						{(!filteredData || !filteredData.length) && (
							<tr>
								<td style={{ textAlign: "center", height: "200px" }} rowSpan={3} colSpan={7}>
									{t("no")} {t("agreements")} {t("to-display")}
								</td>
							</tr>
						)}
						{filteredData &&
							filteredData.map((element, idx) =>
								element[1].map((ur, index) => (
									<DefaultAgreementRolesRow
										key={idx + index}
										groupId={idx}
										index={idx + index}
										user={ur}
										refetch={() => refetch}
										orgId={orgId}
									/>
								))
							)}
					</tbody>
				</table>
			</Box>
			{loading && (
				<Box width="100%" height="100%" display="flex" justifyContent="center" alignItems="center">
					{" "}
					<Loading />
				</Box>
			)}
		</Fragment>
	);
};

export default DefaultAgreementRolesAction;
